import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {SelectButton} from "primereact/selectbutton";
import {Tag} from "primereact/tag";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {formatDistanceM} from "@reva/shared/number-format";
import {limitPoisPerType, resolveVehicleForPoi} from "@reva/shared/poi";
import {distance} from "ol/coordinate";
import {boundingExtent, buffer, containsCoordinate} from "ol/extent";
import {fromLonLat} from "ol/proj";

import AppreciationTabInputText from "../../component/appreciation/AppreciationTabInputText";
import ErrorIndicator from "../../component/ErrorIndicator";
import LoadingIndicator from "../../component/LoadingIndicator";
import MapLayerUnavailableHint from "../../component/MapLayerUnavailableHint";
import MissingPermissionIndicator from "../../component/MissingPermissionIndicator";
import OpenLayersMap from "../../component/OpenLayersMap";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useBasemapDataYear} from "../../hooks/useBasemapDataYear";
import {useMapSettings} from "../../hooks/useMapSettings";
import {usePdfPreview} from "../../hooks/usePdfPreview";
import {selectAddressData} from "../../redux/addressSlice";
import {selectPoisDataAggregated} from "../../redux/locationInfoSelectors";
import {
    selectPoisError,
    selectPoisLoading,
    selectSelectedVehicle,
    selectShowTrafficMap,
    setSelectedVehicle,
    setShowTrafficMap,
} from "../../redux/locationInfoSlice";
import {LocationInfoEndpoint, queryLocationInfoEndpointAsync} from "../../redux/locationInfoThunk";
import {selectSingleDocProperty} from "../../redux/objectParametersSlice";
import {updateParamAndValidateAndSubmitFormThunk} from "../../redux/objectParametersThunk";
import {DOC_PROPERTY_POIS_PER_TYPE, LocationPdfType} from "../../redux/pdfConstants";
import {hidePdfPreview} from "../../redux/pdfPreviewSlice";
import {selectDarkTheme} from "../../redux/themeSlice";
import {LAYER_BASEMAP, LAYER_POIS_DECORATED, selectCapabilities} from "../../redux/wmtsSlice";
import {
    createLayerSwitcher,
    createLuftbildLayer,
    createScaleView,
    EPSG_3857,
    LayerFactory,
    logMapDebug,
} from "../../util/maps";
import {PERMISSION_RULE_LOCATION_POIS} from "../../util/permissions";
import {POIS_VEHICLE_TO_ICON} from "../../util/pois";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";

/** @typedef {import("ol/format").GeoJSON} GeoJSON */

/** Default scale denominator for the traffic (Anbindung) map (1:30000) -- auch von der Mini-Karte
 * im Änderungsdialog der Beschreibungs-Keywords verwendet (siehe DescriptionRichTextField). */
export const DEFAULT_TRAFFIC_SCALE = 30000;
/** Default scale denominator for the nearby (Versorgung) map (1:5000) -- s.o. */
export const DEFAULT_NEARBY_SCALE = 5000;
/** Entfernungsringe (m) der Anbindungs-Karte -- s.o. */
export const DEFAULT_TRAFFIC_RADII = [1000, 2000, 5000];
/** Entfernungsringe (m) der Versorgungs-Karte -- s.o. */
export const DEFAULT_NEARBY_RADII = [250, 500, 1000];

/**
 *
 * @returns {React.JSX.Element}
 */
function PoisLocationNote() {
    const {t} = useTranslation();
    return (
        <>
            <h2>{t("pois.note_title")}</h2>
            <p>{t("pois.note_basis")}</p>
            <p>{t("pois.note_display")}</p>
            <p>{t("pois.note_sources")}</p>
            <p>
                {t("pois.note_source_osm")} <br /> https://www.openstreetmap.org/copyright
            </p>
            <p>{t("pois.note_source_bbsr")}</p>
        </>
    );
}

const vehicles = ["all", "car", "bike", "foot"];

/**
 *
 * @returns {React.JSX.Element}
 */
function PoisTables({vehicle, features, selectedFeature, rowEvent}) {
    const {t, language} = useRevaTranslation();
    const isDarkTheme = useSelector(selectDarkTheme);

    function featureToRowMapper(feature, vehicleMeta) {
        if (!feature.properties) return {};
        const point = feature.geometry.coordinates;
        const properties = feature.properties;
        const vehicle = resolveVehicleForPoi(vehicleMeta, properties);
        return {
            uid: properties["poi_uid"],
            type: properties[`type_${language}`],
            name: properties["name"],
            duration: properties[`${vehicle}_duration_min`],
            distance: properties[`${vehicle}_path_length_m`],
            category: properties[`category_${language}`],
            latlon: point,
            category_key: properties["category_en"],
            vehicle: vehicle,
            isDarkTheme: isDarkTheme,
        };
    }

    const tableMapper = (vehicleMeta) => {
        const values = features.map((feature) => featureToRowMapper(feature, vehicleMeta));
        //sortiere zunächst nach distance und dann nach duration
        return values.sort((e1, e2) => {
            if (e1.distance && !e2.distance) return -1;
            if (e2.distance && !e1.distance) return 1;
            if (e1.distance === e2.distance) return e1.duration - e2.duration;
            return e1.distance - e2.distance;
        });
    };
    const rows = tableMapper(vehicle);
    const selectedRow = selectedFeature != null ? (rows.find((r) => r.uid === selectedFeature) ?? null) : null;
    const categoryTemplate = (feature) => {
        const classname = feature.category_key?.toLowerCase().replace(" ", "");
        if (!classname) return <p></p>;
        return <Tag value={feature.category} id={`row${feature.uid}`} className={classname}></Tag>;
    };
    const durationTemplate = (feature) => {
        const iconName = isDarkTheme ? `${feature.vehicle}_white` : feature.vehicle;
        return (
            <div className="flex items-center">
                <span className="w-12">{feature.duration ? `${feature.duration} min` : null}</span>
                <img alt="dropdown icon" src={POIS_VEHICLE_TO_ICON[iconName]} style={{width: "20px"}} />
            </div>
        );
    };
    const distanceTemplate = (feature) => (feature.distance ? formatDistanceM(feature.distance, language) : null);

    return (
        <DataTable
            value={rows}
            dataKey="uid"
            selectionMode="single"
            size="small"
            rowClassName="text-sm"
            scrollable
            scrollHeight="flex"
            onRowSelect={rowEvent}
            selection={selectedRow}
            sortField="category"
            sortOrder={1}
        >
            <Column header={t("pois.table_header.type")} sortable field="type" className="w-48"></Column>
            <Column header={t("pois.table_header.category")} sortable body={categoryTemplate} className="w-32"></Column>
            <Column header={t("pois.table_header.name")} sortable field="name"></Column>
            <Column header={t("pois.table_header.distance")} sortable body={distanceTemplate}></Column>
            <Column header={t("pois.table_header.duration")} sortable body={durationTemplate}></Column>
        </DataTable>
    );
}

/**
 *
 * @returns {React.JSX.Element}
 */
function PoisMapAndTable() {
    const {t, language} = useRevaTranslation();

    const vehicleToLabel = {
        all: t("pois.vehicle_option.all"),
        car: t("pois.vehicle_option.car"),
        bike: t("pois.vehicle_option.bike"),
        foot: t("pois.vehicle_option.foot"),
    };
    // Stale-while-revalidate: Beim Ändern der Anzahl (o. jeder Neu-Abfrage) setzt der Slice
    // die POIS-Daten kurzzeitig auf null (loading). Ohne den letzten Stand würde die ganze
    // Komponente ausgehängt (Karte + Tabellen verschwinden -> „schwarz"). Wir halten den
    // letzten guten Stand und zeigen ihn weiter, bis die neuen Daten da sind.
    const poisDataFromStore = useSelector(selectPoisDataAggregated);
    // true, während neu geladen wird (Anzahl/Thema geändert) und schon Daten da sind ->
    // Controls dezent deaktivieren + kleinen Spinner zeigen statt großem Lade-Indikator.
    const poisReloading = useSelector(selectPoisLoading);
    const lastPoisDataRef = useRef(null);
    if (poisDataFromStore) lastPoisDataRef.current = poisDataFromStore;
    const poisData = poisDataFromStore ?? lastPoisDataRef.current;
    const addressData = useSelector(selectAddressData);

    const capabilities = useSelector(selectCapabilities);

    // Anzahl der Orte je Typ: eine Einstellung, gilt für beide Karten-Themen.
    const maxPoisPerType = useSelector((state) => selectSingleDocProperty(state, DOC_PROPERTY_POIS_PER_TYPE)) ?? 1;
    const dispatch = useDispatch();

    const poisCapabilities = capabilities?.[LAYER_POIS_DECORATED];
    const basemapCapabilities = capabilities?.[LAYER_BASEMAP];
    const basemapYear = useBasemapDataYear();

    // Gespeicherte Ansichten ohne sichtbare Adresse verwerfen (#19447).
    const mapAnchor = addressData?.address ?? null;
    const {initialView: trafficInitialView, onMapMoveEnd: onTrafficMoveEnd} = useMapSettings(
        "traffic_map_settings",
        mapAnchor,
    );
    const {initialView: nearbyInitialView, onMapMoveEnd: onNearbyMoveEnd} = useMapSettings(
        "nearby_map_settings",
        mapAnchor,
    );

    const vehicleItems = vehicles.map((v) => {
        return {value: v, label: vehicleToLabel[v]};
    });
    const shownPoiOptions = [
        {value: "traffic", label: t("pois.theme_traffic")},
        {value: "nearby", label: t("pois.theme_nearby")},
    ];
    const showTrafficMap = useSelector(selectShowTrafficMap);
    const pdfPreview = usePdfPreview(showTrafficMap ? LocationPdfType.TRAFFIC : LocationPdfType.NEARBY);
    const activeInitialView = showTrafficMap ? trafficInitialView : nearbyInitialView;
    const activeOnMoveEnd = showTrafficMap ? onTrafficMoveEnd : onNearbyMoveEnd;
    // Gewähltes Verkehrsmittel im Redux-Slice halten (wie showTrafficMap), damit es beim
    // Tab-Wechsel nicht auf "Alle" zurückfällt.
    const vehicle = useSelector(selectSelectedVehicle);
    const [extent, setExtent] = useState(undefined);
    const [highlightedPoi, setHighlightedPoi] = useState(-1);
    const [selectedFeature, setSelectedFeature] = useState(undefined);
    const [bounceStartTime, setBounceStartTime] = useState(undefined);
    const [bounceHeight, setBounceHeight] = useState(0);
    const viewResetCounterRef = useRef(0);
    const prevShowTrafficRef = useRef(showTrafficMap);
    if (prevShowTrafficRef.current !== showTrafficMap) {
        prevShowTrafficRef.current = showTrafficMap;
        viewResetCounterRef.current += 1;
        if (extent !== undefined) {
            setExtent(undefined);
        }
    }
    const maxDurationInMs = 500;
    const maxHeightInPx = 25;
    const intervalInMs = 25;

    useEffect(() => {
        let bounce;
        if (bounceStartTime === undefined) return;

        bounce ??= setInterval(() => {
            const currentDuration = Date.now() - bounceStartTime;
            if (currentDuration > maxDurationInMs) {
                setBounceStartTime(undefined);
                setBounceHeight(0);
                logMapDebug("Ending POI animation");
                clearInterval(bounce);
                bounce = null;
                return;
            }

            const displacement =
                currentDuration < maxDurationInMs / 2 ? currentDuration : maxDurationInMs - currentDuration;
            setBounceHeight((displacement * maxHeightInPx) / maxDurationInMs);
        }, intervalInMs);

        if (bounce) return () => clearInterval(bounce);
    }, [bounceStartTime]);

    // #19906 fehlt eine der beiden Basisebenen (Capabilities nicht ladbar), wird die Karte mit der anderen
    // gezeichnet; MapLayerUnavailableHint nennt die fehlende Ebene. Die Tabellen bleiben ohnehin nutzbar.
    if (!poisCapabilities) {
        logMapDebug(`Keine Eintrag für Layer ${LAYER_POIS_DECORATED} in Capabilities gefunden`);
    }

    if (!poisData || !addressData) {
        return null;
    }

    const defaultScale = showTrafficMap ? DEFAULT_TRAFFIC_SCALE : DEFAULT_NEARBY_SCALE;

    /**
     * @param {SelectButtonChangeEvent} e
     */
    const updateVehicle = (e) => {
        logMapDebug(`Setze Vehicle auf ${e.value} Features`);
        if (e.value && vehicles.includes(e.value)) {
            dispatch(setSelectedVehicle(e.value));
            setHighlightedPoi(-1);
            setSelectedFeature(undefined);
        }
    };

    /**
     * Update the maxPoisPerType and re-query the POIS endpoint with the new value.
     * @param {SelectButtonChangeEvent} e
     */
    const updateMaxRank = (e) => {
        const value = e.value;
        if (value == null || value === maxPoisPerType) return;
        setHighlightedPoi(-1);
        setSelectedFeature(undefined);
        dispatch(updateParamAndValidateAndSubmitFormThunk(DOC_PROPERTY_POIS_PER_TYPE, "DOC_PROPERTY", value, null));
        dispatch(queryLocationInfoEndpointAsync(LocationInfoEndpoint.POIS, value));
    };

    const filterTraffic = (category) =>
        (!showTrafficMap && category !== "Verkehr" && category !== "Gemeindetyp") ||
        ((category === "Verkehr" || category === "Gemeindetyp") && showTrafficMap);

    const address = addressData.address;
    // The API returns a Pareto set that can exceed maxPoisPerType; keep only the first `maxPoisPerType`
    // POIs per type (ordered by time, distance, then name).
    const features = limitPoisPerType(
        poisData.features.filter((f) => filterTraffic(f.properties.category_de)),
        vehicle,
        maxPoisPerType,
    );
    const source = {type: "FeatureCollection", features: features};
    let currentFeature;

    const displayFeatureInfo = (map, pixel) => {
        try {
            const feature = map.forEachFeatureAtPixel(pixel, (feature) => {
                return feature;
            });
            if (!feature) {
                setHighlightedPoi(-1);
                return;
            }
            const featureUid = feature.values_["poi_uid"];
            if (feature !== currentFeature && !feature.id_) {
                setSelectedFeature(featureUid);
            }
            currentFeature = feature;
            const index = poisKeys.indexOf(featureUid);
            setHighlightedPoi(index);
            setExtent([0, 0, 0, 0]);
            document.getElementById(`row${poisKeys[index]}`)?.scrollIntoView({behavior: "smooth", block: "center"});
        } catch (e) {
            console.warn(e);
        }
    };

    const clickHandler = (map, evt) => {
        if (evt.dragging) {
            currentFeature = undefined;
            return;
        }
        const pixel = map.getEventPixel(evt.originalEvent);
        displayFeatureInfo(map, pixel);
    };

    const tooltipHandler = (map, evt) => {
        try {
            const pixel = map.getEventPixel(evt.originalEvent);
            const feature = map.forEachFeatureAtPixel(pixel, (feature) => feature);
            const overlay = map.getOverlayById("tooltipOverlay");
            const tooltip = overlay?.getElement();

            if (!tooltip || !overlay) {
                return;
            }
            if (!feature?.values_?.name) {
                tooltip.style.visibility = "hidden";
                return;
            }

            overlay.setPosition(evt.coordinate);
            tooltip.innerHTML = feature.values_.name;
            tooltip.style.visibility = "visible";
        } catch (e) {
            console.warn(e);
        }
    };

    const mapUpdateHandler = (map) => {
        const mapExtent = map.getView().calculateExtent();
        if (mapExtent.toString() !== extent?.toString()) setExtent(mapExtent);
    };

    const mapModel = {
        view: createScaleView(address, defaultScale, activeInitialView, {
            resetCenterOnChange: true,
            resetCounter: viewResetCounterRef.current,
            extentToFit: extent,
            fixedZoom: true,
        }),
        layers: [
            ...(poisCapabilities
                ? [
                      LayerFactory.createGeoserverWmtsLayer(
                          LAYER_POIS_DECORATED,
                          addressData,
                          undefined,
                          "pois_location_osm",
                          undefined,
                          {
                              ...poisCapabilities,
                              params: {
                                  attributions:
                                      '<a target="_blank" href="https://www.openstreetmap.org/copyright">© OpenStreetMap</a>',
                              },
                              title: "OpenStreetMap",
                              // Basemap ist die Standard-Basisebene; ohne sie springt OpenStreetMap ein (#19906)
                              visible: !basemapCapabilities,
                              type: "base",
                          },
                      ),
                  ]
                : []),
            ...(basemapCapabilities
                ? [
                      LayerFactory.createGeoserverWmtsLayer(
                          LAYER_BASEMAP,
                          undefined,
                          undefined,
                          "pois_location_basemapde",
                          undefined,
                          {
                              params: {
                                  attributions: `© GeoBasis-DE / <a target="_blank" href="https://www.bkg.bund.de">BKG</a> (${basemapYear}) <a target="_blank" href="https://creativecommons.org/licenses/by/4.0/">CC BY 4.0</a>`,
                              },
                              title: "Basemap",
                              type: "base",
                              ...basemapCapabilities,
                          },
                      ),
                  ]
                : []),
            createLuftbildLayer(address.municipality_code, addressData, "pois_location_aerial_imagery"),
            LayerFactory.createCircleLayer(address, showTrafficMap ? DEFAULT_TRAFFIC_RADII : DEFAULT_NEARBY_RADII),
            LayerFactory.createPoisLayer(vehicle, source, [highlightedPoi], null, bounceHeight),
            // Key = Default "markers", damit der "Zurück zum Pin"-Button den Adress-Marker
            // findet (OpenLayersPinControl sucht reva_key === "markers"). highlighted bleibt an.
            LayerFactory.createMarkerLayer(address, language, undefined, true),
        ],
        eventHandlers: {
            click_event: clickHandler,
            map_update_event: mapUpdateHandler,
            hover_feature_event: tooltipHandler,
            // mapUpdateHandler auch hier aufrufen: sonst bleibt extent nach rein manuellem
            // Pan/Zoom (kein Rerender) veraltet, bis irgendein anderer Rerender ihn nachzieht.
            moveend_event: (map) => {
                activeOnMoveEnd(map);
                mapUpdateHandler(map);
            },
        },
        switcher: createLayerSwitcher(t),
        scaleLine: {
            enabled: true,
        },
    };

    const poisKeys = features.map((f) => f.properties["poi_uid"]);
    const showPoi = (event) => {
        //Transformation nach Webmercator für Einheitslänge~1m
        const addressCoord = fromLonLat([address.lon, address.lat], EPSG_3857);
        const poiCoord = fromLonLat(event.data.latlon, EPSG_3857);
        const extentLength = extent === undefined ? 0 : Math.max(extent[2] - extent[0], extent[3] - extent[1]);
        const zoomOutNeeded =
            extent === undefined ? true : !containsCoordinate(buffer(extent, -extentLength * 0.05), poiCoord);
        const zoomInNeeded = extentLength / distance(addressCoord, poiCoord) > 13;

        if (zoomOutNeeded || zoomInNeeded) {
            adjustExtend(addressCoord, poiCoord);
        }
        setBounceStartTime(Date.now());

        const index = poisKeys.indexOf(event.data.uid);
        setSelectedFeature(event.data.uid);
        setHighlightedPoi(index);
    };

    const adjustExtend = (addressCoord, poiCoord) => {
        const deltaX = Math.abs(addressCoord[0] - poiCoord[0]);
        const deltaY = Math.abs(addressCoord[1] - poiCoord[1]);
        //Berechne Kartenausschnitt für Latlon mit Mittelpunkt abgefragte Adresse
        let newExtent = boundingExtent([
            [addressCoord[0] - deltaX, addressCoord[1] - deltaY],
            [addressCoord[0] + deltaX, addressCoord[1] + deltaY],
        ]);
        //Berechne Buffer aus den Kantenlängen des Kartenausschnitts, damit Punkte am Rand gut sichtbar sind
        const dist = Math.hypot(newExtent[2] - newExtent[0], newExtent[3] - newExtent[1]);
        newExtent = buffer(newExtent, dist * 0.2);
        setExtent(newExtent);
    };

    const changeMapTheme = () => {
        dispatch(setShowTrafficMap(!showTrafficMap));
        // Offene PDF-Druckvorschau-Box beim Themenwechsel immer schließen.
        dispatch(hidePdfPreview());
        setExtent(undefined);
        setHighlightedPoi(-1);
        setSelectedFeature(undefined);
    };

    function PoiControls() {
        // Kompakt: je Steuerung eine Zeile (Label links, Control rechts) statt gestapelter
        // Überschriften + umbrechender Button-Reihen -> deutlich weniger vertikaler Platz.
        const selectButtonPt = {
            button: ({context}) => ({
                className: context.selected ? "border text-primary bg-surface-0 dark:bg-surface-900" : "",
            }),
        };
        return (
            <div className="element-panel m-0 mb-4 p-3 flex flex-col gap-2">
                <div className="flex items-center gap-3">
                    <span className="font-bold w-44 shrink-0">{t("location.view")}</span>
                    <SelectButton
                        value={shownPoiOptions[showTrafficMap ? 0 : 1].value}
                        options={shownPoiOptions}
                        onChange={changeMapTheme}
                        allowEmpty={false}
                        disabled={poisReloading}
                        pt={selectButtonPt}
                    />
                </div>
                <div className="flex items-center gap-3">
                    <span className="font-bold w-44 shrink-0">{t("pois.max_rank_label")}</span>
                    <SelectButton
                        value={maxPoisPerType}
                        options={[1, 2, 3, 4]}
                        onChange={updateMaxRank}
                        allowEmpty={false}
                        disabled={poisReloading}
                        pt={selectButtonPt}
                    />
                    {/* Dezentes Lade-Feedback beim Nach-Laden (statt großem Overlay). */}
                    {poisReloading && <i className="pi pi-spin pi-spinner text-primary ml-1" aria-hidden="true" />}
                </div>
                <div className="flex items-center gap-3">
                    <span className="font-bold w-44 shrink-0">{t("pois.vehicle_option_label")}</span>
                    <SelectButton
                        value={vehicle}
                        options={vehicleItems}
                        onChange={updateVehicle}
                        disabled={poisReloading}
                        pt={selectButtonPt}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="MapSplit h-full pois-wide-left">
            <div className="MapSplitLeft flex flex-col">
                {/* Scroll-Containment wie in den anderen Lage-Tabs: Controls UND Tabelle scrollen
                    gemeinsam intern; die Karte bleibt immer gleich hoch. */}
                <div className="flex-1 relative">
                    <div className="absolute w-full h-full overflow-auto">
                        <PoiControls />
                        <div className="element-panel">
                            <PoisTables
                                vehicle={vehicle}
                                features={features}
                                selectedFeature={selectedFeature}
                                rowEvent={showPoi}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="MapSplitRight OpenLayersMapBackdrop relative">
                <MapLayerUnavailableHint layers={[LAYER_BASEMAP, LAYER_POIS_DECORATED]} />
                <OpenLayersMap
                    key={showTrafficMap ? "traffic" : "nearby"}
                    model={mapModel}
                    legend={<PoisLocationNote />}
                    legendOptions={{buttonLabel: t("general.map.note")}}
                    pdfPreview={pdfPreview}
                />
            </div>
        </div>
    );
}

/**
 *
 * @returns {React.JSX.Element}
 */
export function TabPoisLocation() {
    const poisLoading = useSelector(selectPoisLoading);
    const poisError = useSelector(selectPoisError);
    // Latch: sobald einmal Daten geladen waren, gilt der Tab als "hatte schon Daten".
    // (Der Live-Selektor wird beim Nach-Laden kurz null -> würde sonst den großen
    // Indikator fälschlich auch beim Nach-Laden zeigen.)
    const poisDataLoaded = useSelector(selectPoisDataAggregated) != null;
    const everLoadedRef = useRef(false);
    if (poisDataLoaded) everLoadedRef.current = true;

    return (
        <>
            <div id="info" className="tooltip"></div>
            <ErrorIndicator errorObject={poisError} titleI18nKey="pois.error" />
            <MissingPermissionIndicator permissionRule={PERMISSION_RULE_LOCATION_POIS} />
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <AppreciationTabInputText page="location" tab="points_of_interest" />
            {/* Großer Lade-Indikator nur beim ERST-Load (noch keine Daten). Beim Nach-Laden
                (Anzahl/Thema geändert) bleiben Karte+Tabellen dank Stale-while-revalidate stehen;
                Feedback dort dezent über deaktivierte Controls + kleinen Spinner (s. PoiControls). */}
            <LoadingIndicator loading={poisLoading && !everLoadedRef.current} />
            <PoisMapAndTable />
        </>
    );
}

export default TabPoisLocation;
